import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import { Phone, ArrowRight } from 'lucide-react';

const FAQPage = () => {
  const faqs = [
    {
      category: "General Questions",
      questions: [
        {
          q: "What areas do you serve?",
          a: "We proudly serve Palm Bay, Melbourne, West Melbourne, Viera, Rockledge, Cocoa, Cocoa Beach, Satellite Beach, Indian Harbour Beach, Titusville, and surrounding communities throughout Brevard County, Florida."
        },
        {
          q: "Are you insured?",
          a: "Yes, Pierce Premier Cleaning Services is fully insured. Your home and property are completely protected, giving you peace of mind every time we clean."
        },
        {
          q: "Do you bring your own cleaning supplies?",
          a: "Yes, we bring all necessary professional-grade cleaning supplies and equipment. If you prefer we use specific products or have particular preferences, we're happy to accommodate your requests."
        },
        {
          q: "Will I have the same cleaning professionals each time?",
          a: "Yes! Unlike larger companies that rotate crews, you'll work with the same trusted professionals every time. This is one of the key benefits of working with a locally owned husband-and-wife business."
        },
        {
          q: "Do you clean homes with pets?",
          a: "Absolutely! We're pet-friendly and understand that pets are family. We'll work around your pets' schedules and treat them with care and respect."
        }
      ]
    },
    {
      category: "Residential & Deep Cleaning",
      questions: [
        {
          q: "What's included in a standard house cleaning?",
          a: "Our standard house cleaning includes dusting, vacuuming, mopping floors, cleaning bathrooms (toilets, sinks, showers, tubs), kitchen cleaning (countertops, sinks, appliances), trash removal, and surface wiping throughout your home."
        },
        {
          q: "What's the difference between regular cleaning and deep cleaning?",
          a: "Regular cleaning maintains your home's cleanliness with routine tasks. Deep cleaning is more intensive and includes detailed scrubbing, cleaning behind and under furniture, baseboards, trim, cabinet interiors, and areas that aren't part of regular maintenance."
        },
        {
          q: "How long does a typical cleaning take?",
          a: "Cleaning time varies based on your home's size, condition, and the type of service. A standard house cleaning typically takes 2-4 hours. We'll provide a more accurate estimate when you request your personalized quote."
        }
      ]
    },
    {
      category: "Move-In & Move-Out Cleaning",
      questions: [
        {
          q: "Do you provide move-out cleaning services?",
          a: "Yes! Move-out cleaning is one of our specialties. We'll thoroughly clean your empty property so it's ready for the next occupants or final walk-through inspection."
        },
        {
          q: "Can you clean on short notice for a move?",
          a: "We'll do our best to accommodate last-minute requests. Contact us as soon as possible, and we'll work with you to schedule your move-in or move-out cleaning."
        },
        {
          q: "What's included in move-in/move-out cleaning?",
          a: "Our move-in/move-out cleaning includes all surfaces, cabinets, closets, appliances, windows, baseboards, and detailed cleaning of bathrooms and kitchens. We clean everything so the property is completely ready for the next phase."
        }
      ]
    },
    {
      category: "Recurring Service & Scheduling",
      questions: [
        {
          q: "Do you offer recurring cleaning services?",
          a: "Yes! We offer weekly, bi-weekly, and monthly recurring cleaning services. Recurring service ensures your home stays consistently clean and gives you the convenience of a regular schedule."
        },
        {
          q: "How do I request a quote?",
          a: "You can request a free quote by calling us at 321-408-1347 or filling out our contact form. We'll discuss your needs and provide a personalized quote based on your home and service requirements."
        },
        {
          q: "Do you offer same-day cleaning?",
          a: "While we focus on scheduled service to ensure quality and consistency, we'll do our best to accommodate urgent requests when possible. Please contact us directly to discuss availability."
        }
      ]
    },
    {
      category: "Commercial & Specialized Services",
      questions: [
        {
          q: "Do you clean commercial properties?",
          a: "Yes, we provide professional cleaning for small offices, retail locations, and commercial spaces throughout Brevard County."
        },
        {
          q: "What is post-construction cleaning?",
          a: "Post-construction cleaning removes all dust, debris, and residue left after construction or renovation work. We ensure your space is completely clean and ready for occupancy or use."
        },
        {
          q: "Do you offer Airbnb and vacation rental cleaning?",
          a: "Yes! We provide fast, reliable turnover cleaning for vacation rentals and Airbnb properties, ensuring your guests arrive to a spotless space every time."
        },
        {
          q: "What is the Home Check-In Service?",
          a: "Our Home Check-In Service provides regular wellness check visits for seniors, combining light housekeeping with safety and home monitoring. It's designed to give families peace of mind when loved ones live independently."
        }
      ]
    }
  ];

  // Create FAQ structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(category => 
      category.questions.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  return (
    <>
      <SEO 
        title="FAQ | Frequently Asked Questions | Pierce Premier Cleaning Services"
        description="Get answers to common questions about our cleaning services, service areas, scheduling, pricing, and what to expect from Pierce Premier Cleaning Services in Brevard County."
        canonical="/faq"
        structuredData={faqStructuredData}
      />

      <div className="min-h-screen pt-20 bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600">
              Everything you need to know about our cleaning services
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  {category.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faqIndex} 
                      value={`item-${categoryIndex}-${faqIndex}`}
                      className="border border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors"
                    >
                      <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Still Have Questions?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              We're here to help! Contact us directly for personalized answers
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-10 py-7">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:3214081347">
                <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-10 py-7">
                  <Phone className="mr-2 w-5 h-5" />
                  Call 321-408-1347
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQPage;
